import sparkle from '@/assets/svgs/icons/sparkle.svg';
import { db } from '@/Firebase';
import { classNames, emptyConverter } from '@/utils';
import { Website } from '@superfunnel/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { ArrowRight, NavArrowDown, NavArrowUp } from 'iconoir-react';
import { useCallback, useEffect, useState } from 'react';
import { VariantInfo, VariantSelection } from './types';

interface WebsiteInfo {
  id: string;
  name?: string;
  status: string;
}

interface VariantListProps {
  variants: VariantInfo[];
  selectedVariant?: VariantSelection;
  dynamicPageId: string;
  dynamicPageName: string;
  onSelect: (selection: VariantSelection) => void;
  accountId: string;
}

export function VariantList({
  variants,
  selectedVariant,
  dynamicPageId,
  dynamicPageName,
  onSelect,
  accountId,
}: VariantListProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [expandedVariantId, setExpandedVariantId] = useState<string | null>(
    null,
  );
  // Cache for loaded website info
  const [websitesCache, setWebsitesCache] = useState<Map<string, WebsiteInfo>>(
    new Map(),
  );
  const [loadingWebsites, setLoadingWebsites] = useState(false);

  const fetchWebsites = useCallback(async () => {
    if (websitesCache.size > 0 || loadingWebsites || !accountId) {
      return;
    }

    setLoadingWebsites(true);

    try {
      const websitesColRef = collection(
        db,
        'accounts',
        accountId,
        'campaigns',
        dynamicPageId,
        'websites',
      ).withConverter(emptyConverter<Website>());

      const websitesSnapshot = await getDocs(
        query(
          websitesColRef,
          where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
          orderBy('createdAt', 'asc'),
        ),
      );

      const websitesMap = new Map<string, WebsiteInfo>();
      websitesSnapshot.docs.forEach((wsDoc) => {
        const websiteData = wsDoc.data();
        websitesMap.set(wsDoc.id, {
          id: wsDoc.id,
          name: websiteData.name || '',
          status: websiteData.status,
        });
      });

      setWebsitesCache(websitesMap);
    } catch (err) {
      console.error('Failed to fetch websites:', err);
    } finally {
      setLoadingWebsites(false);
    }
  }, [accountId, dynamicPageId, websitesCache.size, loadingWebsites]);

  // Fetch websites when a variant with experiments is expanded
  useEffect(() => {
    if (expandedVariantId) {
      fetchWebsites();
    }
  }, [expandedVariantId, fetchWebsites]);

  const getExperimentLabel = (index: number): string => {
    if (index === 0) return 'Control';
    const letter = String.fromCharCode(64 + index);
    return `Experiment ${letter}`;
  };

  const getWebsiteName = (websiteId: string, index: number): string | null => {
    const websiteInfo = websitesCache.get(websiteId);
    if (websiteInfo && websiteInfo.name) {
      return websiteInfo.name;
    }
    if (websiteInfo) {
      return getExperimentLabel(index);
    }
    if (loadingWebsites) {
      return null;
    }
    return getExperimentLabel(index);
  };

  const isWebsiteDraft = (websiteId: string): boolean => {
    const websiteInfo = websitesCache.get(websiteId);
    return websiteInfo?.status === 'draft';
  };

  if (variants.length === 0) {
    return (
      <div className="text-base-500 py-4 text-center text-sm">
        No other variants available
      </div>
    );
  }

  const toggleExpand = (variantId: string) => {
    setExpandedVariantId((prev) => (prev === variantId ? null : variantId));
  };

  return (
    <div className="flex w-full flex-col gap-1">
      {variants.map((variant) => {
        const websiteIds = variant.websiteIds || [];
        const hasExperiment = websiteIds.length > 1;
        const isExpanded = expandedVariantId === variant.id;
        const isVariantSelected = selectedVariant?.variantId === variant.id;
        const isHovered = hoveredId === variant.id;

        // For variants with experiments, show expandable list
        if (hasExperiment) {
          return (
            <div key={variant.id} className="flex flex-col">
              {/* Variant header with expand/collapse */}
              <button
                type="button"
                onClick={() => toggleExpand(variant.id)}
                onMouseEnter={() => setHoveredId(variant.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={classNames(
                  'relative flex items-center justify-between rounded-xl p-2 transition-colors',
                  isVariantSelected ? 'bg-base-100' : 'hover:bg-base-50',
                )}
              >
                <div className="flex items-center gap-[5px]">
                  <div className="flex w-3 items-center justify-center">
                    <img src={sparkle} alt="" className="size-2.5" />
                  </div>
                  <span className="text-base-900 text-sm">{variant.name}</span>
                  <span className="text-base-500 ml-1 text-xs">
                    ({websiteIds.length} tests)
                  </span>
                </div>
                <div className="flex size-6 items-center justify-center rounded-lg">
                  {isExpanded ? (
                    <NavArrowUp
                      className="text-base-500 size-4"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <NavArrowDown
                      className="text-base-500 size-4"
                      strokeWidth={1.5}
                    />
                  )}
                </div>
              </button>

              {/* Expandable test list */}
              {isExpanded && (
                <div className="ml-6 flex flex-col gap-0.5 border-l border-[#e4e7ec] pl-2">
                  {websiteIds.map((websiteId, index) => {
                    const isTestSelected =
                      selectedVariant?.websiteId === websiteId;
                    const testName = getWebsiteName(websiteId, index);
                    const isDraft = isWebsiteDraft(websiteId);

                    const buttonContent = (
                      <button
                        key={websiteId}
                        type="button"
                        disabled={isDraft}
                        onClick={() =>
                          onSelect({
                            variantId: variant.id,
                            variantName: variant.name,
                            websiteId,
                            dynamicPageId,
                            dynamicPageName,
                          })
                        }
                        className={classNames(
                          'relative flex w-full items-center justify-between rounded-lg px-2 py-1.5 transition-colors',
                          isDraft && 'cursor-not-allowed opacity-50',
                          isTestSelected
                            ? 'bg-base-100'
                            : !isDraft && 'hover:bg-base-50',
                        )}
                      >
                        {testName === null ? (
                          <div className="bg-base-200 h-3 w-20 animate-pulse rounded" />
                        ) : (
                          <span className="text-base-700 text-xs">
                            {testName}
                          </span>
                        )}
                        <div
                          className={classNames(
                            'flex items-center gap-1 transition-opacity',
                            isTestSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        >
                          <ArrowRight
                            className="text-brand-900 size-3"
                            strokeWidth={1}
                          />
                        </div>
                      </button>
                    );

                    if (isDraft) {
                      return (
                        <Tooltip key={websiteId}>
                          <TooltipTrigger asChild>
                            {buttonContent}
                          </TooltipTrigger>
                          <TooltipContent side="left" sideOffset={4}>
                            This is a draft test and cannot be selected
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return buttonContent;
                  })}
                </div>
              )}
            </div>
          );
        }

        // For variants without experiments, show simple row
        const websiteId = websiteIds[0];
        const isSelected =
          selectedVariant?.variantId === variant.id &&
          selectedVariant?.websiteId === websiteId;

        return (
          <button
            key={variant.id}
            type="button"
            onClick={() =>
              onSelect({
                variantId: variant.id,
                variantName: variant.name,
                websiteId: websiteId || '',
                dynamicPageId,
                dynamicPageName,
              })
            }
            onMouseEnter={() => setHoveredId(variant.id)}
            onMouseLeave={() => setHoveredId(null)}
            className={classNames(
              'relative flex items-center justify-between rounded-xl p-2 transition-colors',
              isSelected ? 'bg-base-100' : 'hover:bg-base-50',
            )}
          >
            <div className="flex items-center gap-[5px]">
              <div className="flex w-3 items-center justify-center">
                <img src={sparkle} alt="" className="size-2.5" />
              </div>
              <span className="text-base-900 text-sm">{variant.name}</span>
            </div>
            <div
              className={classNames(
                'flex items-center gap-1 rounded-lg transition-opacity',
                isHovered || isSelected ? 'opacity-100' : 'opacity-0',
              )}
            >
              <span className="text-base-900 text-xs">Select</span>
              <ArrowRight className="text-brand-900 size-4" strokeWidth={1} />
            </div>
          </button>
        );
      })}
    </div>
  );
}
